import React from "react";
import { useNavigate } from "react-router-dom";
import { isEntitled, isCatalog, menuEntitled } from "../tenancy";

/*
  MODULE GUARD — mode ke darwaze par ek saaf jawab.

  ─────────────────────────────────────────────────────────────────────────────
  KYUN BANA

  T11.5 me har module ke GET endpoints par gate laga. Backend ab sahi rokta hai.
  Par frontend par koi rok nahi thi, to end-to-end test me ye nikla:

      ZZTH_opd (sirf OPD module) -> /hrms/dashboard
      backend:  Hrms/dashboard, widgets, setup-health  ->  403
      screen :  HRMS dashboard poora render, har jagah 0

  Data leak nahi hua — par user ko lagta hai "koi employee hi nahi hai".
  Wahi /ward/dashboard par bhi: "0 Admitted".

  Ye jhooth khatarnak hai. Nurse ward khole aur "0 Admitted" dekhe to wo maan
  legi ki ward khaali hai. Isliye darwaze par hi saaf jawab dena zaroori hai.

  ─────────────────────────────────────────────────────────────────────────────
  JAANCH KAISE HOTI HAI

  1. `isHospitalAdmin` — Tier 1 ko sab kuch. Ye zaroori hai kyunki hospital admin
     ka adhikar FLAG se aata hai; uske paas har module ki `UserRoles` row ho ye
     zaroori nahi. Iske bina guard admin ko hi rok deta.

  2. `allowedModules` — login response se aata hai aur backend use ASLI adhikar
     se banata hai (`AuthController`: Add/Edit/Delete me se koi 'Y' ho tabhi wo
     module list me aata hai). Isliye ispar bharosa kiya ja sakta hai.

  0. (Phase 28, sirf shared website) module hospital ke PLAN me hi nahi → kisi ko nahi,
     hospital admin ko bhi nahi. Single mode (Kamla / keycodehms) me ye kadam hamesha "haan".

  ⚠️ Ye SIRF UI ka aaram hai, suraksha nahi. Asli rok backend ke gate hain.
     Koi sessionStorage badal kar andar aa bhi jaye to har API 403 hi degi.
*/

export function hasModule(name) {
  try {
    // "MASTER" = Central Master (users, roles, hospital profile). Hospital admin ko HAMESHA —
    // shared website par sirf-Pharmacy wale hospital ko bhi apne user banane hain. Baaki ke
    // liye pehle jaisa HMS. Single mode me farq nahi (wahan admin ko HMS pehle se milta hai).
    if (String(name).trim().toUpperCase() === "MASTER") {
      if (sessionStorage.getItem("isHospitalAdmin") === "1") return true;
      return hasModule("HMS");
    }

    // 0. Plan (shared website) — ye module hospital ko mila hi nahi
    if (!isEntitled(name)) return false;

    if (sessionStorage.getItem("isHospitalAdmin") === "1") return true;
    const raw = sessionStorage.getItem("allowedModules");
    if (!raw) return false;
    const mods = JSON.parse(raw).map((m) => String(m).trim().toUpperCase());

    const want = String(name).trim().toUpperCase();
    if (mods.includes(want)) return true;

    // ── HMS wale ko kahan andar aane dena hai ───────────────────────────────
    //
    // ⚠️ Ye jaanch pehle THI HI NAHI, aur usse teen jagah ki soch alag ho gayi:
    //
    //     backend  : Pharmacy ka gate `CanView(1) || CanView(3)` hai
    //                Ward ka `CanView(1) || CanView(15)` -- dono HMS ko lene dete hain
    //     Sidebar  : `hasAccess` me HMS par `return true` -- menu DIKHTA hai
    //     ye guard : HMS ko maanta hi nahi tha -- andar jaate hi ROK
    //
    // Live par pakda gaya: `DevUser` ke paas HMS=YYY tha. Use Pharmacy ka menu
    // dikhta tha, par click karte hi "You do not have permission" -- jabki
    // backend uska kaam maan raha tha. Wahi Ward par bhi.
    //
    // ⚠️ Ye soochi backend se MEL khani chahiye -- na zyada, na kam.
    //
    //    Backend me niyam ek hi jagah hai: `PermissionService.HmsBhiKholta`
    //    (aur baaki module ke controller me `|| CanView(1)`).
    //
    //    Yahan zyada likh diya  -> user andar chala jayega par har API 403 degi,
    //                              aur screen par har jagah 0 dikhega. Wahi
    //                              jhooth jise rokne ke liye ye guard bana tha.
    //    Yahan kam likh diya    -> backend allow karega par screen rok degi.
    //                              (29 Aug ko live par yahi hua tha -- HMS wale
    //                               ko Pharmacy ka menu dikhta tha par andar
    //                               jaate hi rok lagti thi.)
    //
    // HRMS 29 Aug 2026 ko joda gaya -- user ka faisla. Uske saath backend me
    // `HmsBhiKholta` me bhi 16 jodna zaroori tha; dono saath badalte hain.
    const HMS_KHOLTA_HAI = ["HMS", "PHARMACY", "IPD WARD", "INVENTORY", "HRMS"];

    if ((mods.includes("HMS") || mods.includes("ADMIN")) && HMS_KHOLTA_HAI.includes(want)) {
      return true;
    }

    return false;
  } catch {
    // sessionStorage padha na ja sake to rok mat lagao — backend waise bhi rokta hai,
    // aur yahan galat rok lagana asli kaam rok deti hai.
    return true;
  }
}

const GUARD_CSS = `
  .mg-wrap { display:flex; align-items:center; justify-content:center; min-height:80vh; padding:2rem; }
  .mg-box { text-align:center; max-width:460px; }
  .mg-ic { font-size:3rem; color:#94a3b8; }
  .mg-ttl { margin:14px 0 8px; color:#0f172a; font-weight:700; }
  .mg-txt { color:#64748b; font-size:.9rem; line-height:1.6; margin-bottom:20px; }
  .mg-btn { padding:9px 20px; border:none; border-radius:8px; background:#0088cc; color:#fff; font-weight:600; cursor:pointer; }
`;

function GuardBox({ icon, title, text }) {
  const navigate = useNavigate();
  return (
    <div className="mg-wrap">
      <div className="mg-box">
        <i className={`bi ${icon} mg-ic`}></i>
        <h4 className="mg-ttl">{title}</h4>
        <p className="mg-txt">{text}</p>
        <button className="mg-btn" onClick={() => navigate("/dashboard")}>
          Back to dashboard
        </button>
      </div>
      <style>{GUARD_CSS}</style>
    </div>
  );
}

// Shared website: module hospital ke plan me hi nahi — "admin se maango" galat salah hoti,
// admin bhi nahi de sakta. Isliye alag sandesh.
export function NotInPlan({ name }) {
  return (
    <GuardBox
      icon="bi-bag-x"
      title={`${name} is not in your plan`}
      text={`Your hospital's plan does not include the ${name} module. To add it, please contact support.`}
    />
  );
}

const ModuleGuard = ({ module: moduleName, label, children }) => {
  if (hasModule(moduleName)) return children;

  const name = label || moduleName;
  if (isCatalog() && String(moduleName).trim().toUpperCase() !== "MASTER" && !isEntitled(moduleName)) {
    return <NotInPlan name={name} />;
  }

  return (
    <GuardBox
      icon="bi-shield-lock"
      title={`${name} access required`}
      text={`You do not have permission to open the ${name} module. Ask your hospital admin to give you access.`}
    />
  );
};

// SIRF plan ki jaanch (Phase 28) — un mode ke liye jin par aaj koi ModuleGuard nahi hai
// (/inventory, /clinical, /billing, /admin-reports). Single mode me hamesha andar jaane deta
// hai, yaani Kamla/keycodehms par in raaston ka vyavhaar bilkul pehle jaisa.
// `module` Sidebar jaisa: ek naam ya soochi (["OPD", "IPD", "HMS"]).
export const PlanGuard = ({ module, label, children }) => {
  if (menuEntitled(module)) return children;
  const first = Array.isArray(module) ? module[0] : module;
  return <NotInPlan name={label || first} />;
};

export default ModuleGuard;
